import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, RotateCcw, CheckCircle2, Loader2, Send, ShieldOff, ShieldCheck, Mail, MessageSquare, RefreshCw } from "lucide-react";

// Clipboard with textarea fallback
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Parent link copied");
  } catch {
    toast.error("Could not copy — please use the open button instead");
  }
};

const STATUS_STYLE = {
  "Active":        "bg-emerald-100 text-emerald-700",
  "Pending":       "bg-amber-100 text-amber-700",
  "Trial Active":  "bg-blue-100 text-blue-700",
  "Trial Ended":   "bg-red-100 text-red-700",
  "Paused":        "bg-rose-100 text-rose-700",
  "Needs Invite":  "bg-orange-100 text-orange-700",
  "Inactive":      "bg-slate-100 text-slate-500",
  "Needs Review":  "bg-red-200 text-red-800",
};

export const ParentBillingChildrenCard = ({
  rows,
  canActivate,
  onToggleChild,
  onRestoreChild,
  onManualActivate,
  onSendInvite,
  onRevokeAccess,
  onReinstateAccess,
  onResetInvite,
  processingChildId,
  restoringChildId,
  activatingChildId,
  sendingInviteChildId,
  revokingChildId,
}) => {
  // Per-row delivery toggle state: { [childId]: { email: true, sms: false } }
  const [deliveryToggles, setDeliveryToggles] = useState({});

  const getToggles = (childId) =>
    deliveryToggles[childId] ?? { email: true, sms: false };

  const setToggle = (childId, field, value) =>
    setDeliveryToggles((prev) => ({
      ...prev,
      [childId]: { ...getToggles(childId), [field]: value },
    }));

  return (
    <Card
      className={`border-0 shadow-xl ring-1 ${canActivate ? "bg-white/90 ring-emerald-100" : "bg-slate-50/80 ring-slate-200"}`}
      data-testid="parent-billing-children-card"
    >
      <CardHeader>
        <CardTitle className="text-xl text-slate-900">Individual child activation list</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-800" data-testid="parent-billing-children-instruction">
          Check a child to create their invite, then choose <span className="font-semibold">Email</span> and/or <span className="font-semibold">Text</span> and hit <span className="font-semibold text-emerald-700">Send Invite</span>.
          When a parent stops paying, click <span className="font-semibold text-red-600">Pause</span> to suspend access (reversible). Use <span className="font-semibold text-rose-700">Reset Invite</span> to issue a brand-new link.
        </div>

        <div className="overflow-hidden rounded-[1.4rem] border border-emerald-100">
          <div className="overflow-auto max-h-[520px]">
            {/* Header */}
            <div
              className="grid min-w-[900px] grid-cols-[44px_1.2fr_1.2fr_80px_130px_1fr] gap-3 bg-emerald-50 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-slate-700 sticky top-0 z-10"
              data-testid="parent-billing-children-header"
            >
              <span></span>
              <span>Child Name</span>
              <span>Parent Email</span>
              <span className="text-right">Fee</span>
              <span className="text-center">Status</span>
              <span className="text-center">Action</span>
            </div>

            {rows.length === 0 ? (
              <div className="p-5 text-center text-sm text-slate-500" data-testid="parent-billing-children-empty">
                No enrolled children found.
              </div>
            ) : (
              rows.map((row) => {
                const toggles = getToggles(row.id);
                const isSending = sendingInviteChildId === row.id;
                const isRevoking = revokingChildId === row.id;
                const isActivating = activatingChildId === row.id;
                const isRestoring = restoringChildId === row.id;
                const isProcessing = processingChildId === row.id;

                return (
                  <div
                    key={row.id}
                    className="grid min-w-[900px] grid-cols-[44px_1.2fr_1.2fr_80px_130px_1fr] gap-3 border-t border-emerald-100 bg-white px-3 py-3 text-xs items-start"
                    data-testid={`parent-billing-child-row-${row.id}`}
                  >
                    {/* Checkbox */}
                    <div className="flex items-center justify-center pt-1">
                      <input
                        type="checkbox"
                        checked={row.billingActive}
                        disabled={!canActivate || isProcessing}
                        onChange={(e) => onToggleChild(row.id, e.target.checked)}
                        className="h-4 w-4 accent-emerald-600 disabled:opacity-50"
                        data-testid={`parent-billing-child-checkbox-${row.id}`}
                      />
                    </div>

                    {/* Child name */}
                    <div className="truncate font-medium text-slate-900 pt-1">{row.childName}</div>

                    {/* Parent email */}
                    <div className="truncate text-slate-600 pt-1">{row.parentEmail}</div>

                    {/* Fee */}
                    <div className="text-right font-semibold text-emerald-700 pt-1">
                      ${Number(row.fee || 0).toFixed(2)}
                    </div>

                    {/* Status badge */}
                    <div className="flex items-center justify-center pt-1">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold cursor-help ${STATUS_STYLE[row.status] || "bg-slate-100 text-slate-500"}`}
                        title={row.statusDetail || row.status}
                        data-testid={`parent-billing-child-status-${row.id}`}
                      >
                        {row.status}
                      </span>
                    </div>

                    {/* Action column */}
                    <div className="flex flex-col gap-1.5">

                      {/* ── NEEDS REVIEW: ambiguous household match, withhold all mutation actions ── */}
                      {row.status === "Needs Review" && (
                        <span className="text-[11px] text-red-600 font-medium pt-1" data-testid={`needs-review-${row.id}`}>
                          Contact support to resolve
                        </span>
                      )}

                      {/* ── INACTIVE: no action ── */}
                      {!row.billingActive && row.status !== "Needs Review" && (
                        <span className="text-[11px] text-slate-400 pt-1">Check to activate</span>
                      )}

                      {/* ── ACTIVATED + NO INVITE YET ("Needs Invite") ── */}
                      {row.billingActive && !row.inviteLink && (
                        <span className="text-[11px] text-slate-400 pt-1">Saving...</span>
                      )}

                      {/* ── PENDING (invite exists, not yet sent OR already sent) ── */}
                      {row.billingActive && row.inviteLink && row.status === "Pending" && (
                        <>
                          {/* Delivery toggles */}
                          <div className="flex items-center gap-2">
                            <label className={`flex items-center gap-1 cursor-pointer px-2 py-1 rounded-lg border text-[11px] font-semibold transition ${toggles.email ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-400"}`}
                              data-testid={`email-toggle-${row.id}`}>
                              <input type="checkbox" className="hidden" checked={toggles.email}
                                onChange={(e) => setToggle(row.id, "email", e.target.checked)} />
                              <Mail className="w-3 h-3" /> Email
                            </label>
                            <label className={`flex items-center gap-1 cursor-pointer px-2 py-1 rounded-lg border text-[11px] font-semibold transition ${toggles.sms ? "border-purple-400 bg-purple-50 text-purple-700" : "border-slate-200 text-slate-400"}`}
                              data-testid={`sms-toggle-${row.id}`}>
                              <input type="checkbox" className="hidden" checked={toggles.sms}
                                onChange={(e) => setToggle(row.id, "sms", e.target.checked)} />
                              <MessageSquare className="w-3 h-3" /> Text
                            </label>
                          </div>

                          {/* Send Invite button */}
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              size="sm"
                              disabled={isSending || (!toggles.email && !toggles.sms)}
                              onClick={() => onSendInvite(row.id, toggles.email, toggles.sms)}
                              className="h-7 rounded-full bg-emerald-600 px-3 text-[11px] text-white hover:bg-emerald-700 disabled:opacity-50"
                              data-testid={`send-invite-btn-${row.id}`}
                            >
                              {isSending
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <><Send className="h-3 w-3 mr-1" />{row.inviteSentAt ? "Resend" : "Send Invite"}</>}
                            </Button>

                            {/* Copy & preview — always available once invite exists */}
                            <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0"
                              onClick={() => copyToClipboard(row.inviteLink)}
                              data-testid={`copy-link-${row.id}`} title="Copy link">
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0"
                              onClick={() => window.open(row.inviteLink, "_blank", "noopener,noreferrer")}
                              data-testid={`open-link-${row.id}`} title="Preview signup page">
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                            <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0"
                              disabled={isRestoring}
                              onClick={() => onResetInvite(row.id)}
                              data-testid={`reset-invite-${row.id}`} title="Reset invite (new link, clears password)">
                              {isRestoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                            </Button>
                          </div>

                          {/* Sent confirmation */}
                          {row.inviteSentAt && (
                            <p className="text-[10px] text-slate-400">
                              Sent via {(row.inviteSentVia || []).join(" + ") || "—"}
                            </p>
                          )}
                        </>
                      )}

                      {/* ── TRIAL ENDED: Activate + Pause + Reset ── */}
                      {row.status === "Trial Ended" && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button type="button" size="sm"
                            className="h-7 rounded-full bg-emerald-600 px-2.5 text-[10px] text-white hover:bg-emerald-700"
                            disabled={isActivating}
                            onClick={() => onManualActivate(row.id)}
                            data-testid={`activate-btn-${row.id}`}>
                            {isActivating ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="mr-1 h-3 w-3" />Activate</>}
                          </Button>
                          <Button type="button" size="sm" variant="outline"
                            className="h-7 rounded-full border-red-300 text-red-600 hover:bg-red-50 px-2.5 text-[10px]"
                            disabled={isRevoking}
                            onClick={() => onRevokeAccess(row.id)}
                            data-testid={`pause-btn-${row.id}`}>
                            {isRevoking ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShieldOff className="mr-1 h-3 w-3" />Pause</>}
                          </Button>
                          <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0 border-rose-300 text-rose-600 hover:bg-rose-50"
                            disabled={isRestoring}
                            onClick={() => onResetInvite(row.id)}
                            data-testid={`reset-invite-${row.id}`} title="Reset invite (new link, clears password)">
                            {isRestoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          </Button>
                        </div>
                      )}

                      {/* ── PAUSED: Reinstate + Reset ── */}
                      {row.status === "Paused" && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button type="button" size="sm"
                            className="h-7 rounded-full bg-emerald-600 px-2.5 text-[10px] text-white hover:bg-emerald-700"
                            disabled={isRevoking}
                            onClick={() => onReinstateAccess(row.id)}
                            data-testid={`reinstate-btn-${row.id}`}>
                            {isRevoking ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShieldCheck className="mr-1 h-3 w-3" />Reinstate</>}
                          </Button>
                          <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0 border-rose-300 text-rose-600 hover:bg-rose-50"
                            disabled={isRestoring}
                            onClick={() => onResetInvite(row.id)}
                            data-testid={`reset-invite-${row.id}`} title="Reset invite (new link, clears password)">
                            {isRestoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          </Button>
                        </div>
                      )}

                      {/* ── TRIAL ACTIVE or ACTIVE: Copy + Open + Pause + Reset ── */}
                      {(row.status === "Trial Active" || row.status === "Active") && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0"
                            onClick={() => copyToClipboard(row.inviteLink)}
                            data-testid={`copy-link-active-${row.id}`} title="Copy link">
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0"
                            onClick={() => window.open(row.inviteLink, "_blank", "noopener,noreferrer")}
                            data-testid={`open-link-active-${row.id}`} title="Preview portal">
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          <Button type="button" size="sm" variant="outline"
                            className="h-7 rounded-full border-red-300 text-red-600 hover:bg-red-50 px-2.5 text-[10px]"
                            disabled={isRevoking}
                            onClick={() => onRevokeAccess(row.id)}
                            data-testid={`pause-btn-${row.id}`}>
                            {isRevoking ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShieldOff className="mr-1 h-3 w-3" />Pause</>}
                          </Button>
                          <Button type="button" variant="outline" className="h-7 w-7 rounded-full p-0 border-rose-300 text-rose-600 hover:bg-rose-50"
                            disabled={isRestoring}
                            onClick={() => onResetInvite(row.id)}
                            data-testid={`reset-invite-${row.id}`} title="Reset invite (new link, clears password)">
                            {isRestoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          </Button>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
